import { useState } from "react"
import { useInertiaCable } from "@inertia-cable/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Button } from "@/components/ui/button"
import { MetricCard } from "@/components/domain/metric-card"
import { AgentTable } from "@/components/domain/agent-table"
import { NewAgentModal } from "@/components/domain/new-agent-modal"
import type { Agent } from "@/types/agent"

// PR #3: live agent data from Portal::AgentPresenter, plus an InertiaCable
// subscription. When ANY Agent / AgentSkill row in this company changes
// (broadcasts_to ->(a) { a.company } in the models), the cable signal fires
// and Inertia calls router.reload({ only: [...] }) to re-fetch just the
// listed props. The page re-renders without a manual refresh.
type Props = {
  agents: Agent[]
  metrics: {
    activeAgents:   { value: string; delta: string }
    ticketsHandled: { value: string; delta: string }
    avgResponse:    { value: string; delta: string }
    vipQuality:     { value: string; delta: string }
  }
  agents_stream: string
}

export default function AgentsIndex({ agents, metrics, agents_stream }: Props) {
  const [newAgentOpen, setNewAgentOpen] = useState(false)

  // Subscribe to the company's signed stream. `only` keeps the partial
  // reload tight — we don't re-fetch shared props (current_user, etc.).
  // 250ms debounce coalesces bursts (e.g. "save 12 skill toggles").
  useInertiaCable(agents_stream, {
    only: ["agents", "skills", "metrics"],
    debounce: 250,
    onConnected:    () => console.log("[InertiaCable] connected to", agents_stream),
    onDisconnected: () => console.log("[InertiaCable] disconnected"),
    onRefresh:      (p) => console.log("[InertiaCable] refresh signal", p),
  })

  return (
    <AppShell>
      <PageHeader
        eyebrow="Agents / All Agents"
        title="AI Support Agents"
        actions={
          <>
            <Button variant="gold">Import skill</Button>
            <Button onClick={() => setNewAgentOpen(true)}>New agent</Button>
          </>
        }
      />

      <section className="grid gap-4 md:grid-cols-4">
        <MetricCard icon="👤" label="Active agents"   value={metrics.activeAgents.value}   delta={metrics.activeAgents.delta} />
        <MetricCard icon="🎟" label="Tickets handled" value={metrics.ticketsHandled.value} delta={metrics.ticketsHandled.delta} />
        <MetricCard icon="⚡" label="Avg. response"   value={metrics.avgResponse.value}    delta={metrics.avgResponse.delta} />
        <MetricCard icon="✦" label="VIP quality"     value={metrics.vipQuality.value}     delta={metrics.vipQuality.delta} />
      </section>

      <AgentTable agents={agents} />

      <NewAgentModal open={newAgentOpen} onClose={() => setNewAgentOpen(false)} />
    </AppShell>
  )
}
